import { NextRouter } from "next/router";
import { Fee } from "../types/fee-types";

// format decimal in US format 0,000.00
/*--------------------------------------------------------------------------------------------------*/
export function formatDecimalString(price: number | string) {
  return price.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// format percentage as US format whole number if possible
/*--------------------------------------------------------------------------------------------------*/
export function formatPercentageString(percent: number) {
  return percent.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function formatPriceDisplay(
  amount: number,
  maxAmount?: number | null,
  variableCosts?: boolean,
  decimalPlaces: number = 2,
  roundUp?: boolean,
): string {
  const variableCostsText = "Usage-based/variable costs";

  if (variableCosts && amount === 0 && !maxAmount) {
    return variableCostsText;
  }

  let processedAmount = amount;
  let processedMaxAmount = maxAmount;

  if (roundUp) {
    processedAmount = Math.ceil(amount);
    processedMaxAmount = maxAmount ? Math.ceil(maxAmount) : null;
  }

  const formattedAmount = processedAmount.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
  let priceString;

  if (
    processedMaxAmount !== undefined &&
    processedMaxAmount !== null &&
    processedMaxAmount !== processedAmount
  ) {
    const formattedMaxAmount = processedMaxAmount.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
    priceString = `$${formattedAmount} \u2013 $${formattedMaxAmount}`;
  } else {
    priceString = `$${formattedAmount}`;
  }

  return variableCosts ? `${priceString} + ${variableCostsText}` : priceString;
}

// Capalize first letter of a string
/*--------------------------------------------------------------------------------------------------*/
export function capitalizeFirstLetter(str: string) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Adds subject to avail to fee group titles
/*--------------------------------------------------------------------------------------------------*/
export function formatFeeGroup(str: string) {
  let groupName = str;
  if (str.includes("parking") || str.includes("storage")) {
    groupName = `${groupName} (subject to availability)`;
  }
  return capitalizeFirstLetter(groupName);
}

// Formats a date string (YYYY-MM-DD) to MM/DD/YYYY format
export function formatDateToMMDDYYYY(dateString?: string): string {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

// Formats a date string (YYYY-MM-DD) to 'Month DD, YYYY' format
export function formatDateToLongMonth(dateString?: string): string {
  if (!dateString) return "";

  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (isNaN(date.getTime())) return "";

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  if (date <= today) return "Now";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    timeZone: "UTC",
  });
}

export const goBackToComponent = (router: NextRouter, id?: string) => {
  // Listen for route change completion
  const handleRouteChange = () => {
    const element = id && document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      // Scroll to top if no id is provided
      document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Clean up the event listener
    router.events.off("routeChangeComplete", handleRouteChange);
  };

  router.events.on("routeChangeComplete", handleRouteChange);

  // Construct url by truncating URL before /calculator and appending #id if provided
  const currentPath = router.asPath;
  const basePath = currentPath.split("/calculator")[0] || "/";
  const targetPath = id ? `${basePath}#${id}` : basePath;

  router.push(targetPath);
};

export function backToFpe(router: NextRouter, componentId?: string) {
  const { asPath } = router;
  const hash = componentId ? `#${componentId}` : "";
  const newPath = asPath.split("?")[0].replace(/\/calculator\/?$/, "") + hash;
  router.push(newPath);
}

export function getValidAvailableDate(availableOn?: string): string {
  if (!availableOn) return "";
  const date = new Date(availableOn);
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  return date <= today ? today.toISOString().split("T")[0] : availableOn;
}

export function getFeeLineLabel(moveInDate: string, fee?: Fee): string {
  if (fee) {
    return fee.label;
  }

  const [, , day] = moveInDate.split("-").map(Number);
  const isFirstDayOfMonth = day === 1;

  if (isFirstDayOfMonth) {
    return `Total monthly leasing price - Based on ${formatDateToMMDDYYYY(moveInDate)} move-in`;
  } else {
    return `Total monthly leasing price - Prorated based on ${formatDateToMMDDYYYY(moveInDate)} move-in`;
  }
}

// Helper function to parse date string to UTC Date object
export function parseDateToUTC(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

// Format phone number as (XXX)XXX-XXXX
export function formatPhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, "");

  if (digits.length !== 10) {
    return "";
  }

  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function sanitizeProps<T>(obj: T): T {
  if (obj === undefined) return null as T;
  if (obj === null || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.map(sanitizeProps) as T;
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v !== undefined)
      .map(([k, v]) => [k, sanitizeProps(v)]),
  ) as T;
}
